import addCssRules from '../cssrules';
import {appendBlockOnClick} from './block';
import i18next from 'i18next';
import type {Component, Editor} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';
import {noop} from '@Utils/general.util';

interface CanvasDropData {
  content: ComponentDropContent[];
}

interface ComponentDropContent {
  type: string;
  attributes: Record<string, any>;
  file: File & {[key: string]: any};
}

/**
 * Listeners related to the Editor
 * @param opt The grapesjs config object
 */
export default (opt: PrimaryConfigOptions): void => {
  const editor = opt.editor;
  window.PMW.isInitializingEditor = true;

  // Do stuff on load
  editor.on('load', async function (): Promise<void> {
    editor.runCommand(window.PMW.emails.commands.GET_PUPPETEER_CONFIG);
    editor.runCommand(window.PMW.emails.commands.INIT_LOADING_TIPS);
    editor.runCommand(window.PMW.emails.commands.REMOVE_PARAMS_FROM_URL);
    editor.runCommand(window.PMW.emails.commands.GET_AUTO_UPDATES);
    editor.runCommand(window.PMW.emails.commands.GET_REMOVE_BG_CREDITS);
    editor.runCommand(window.PMW.emails.commands.ASYNC_GET_SIGNUP_FORMS);
    editor.runCommand(window.PMW.emails.commands.FETCH_SOCIAL_LINK_SUGGESTION);
    editor.runCommand(window.PMW.emails.commands.FINISH_LOADING_EDITOR);
    editor.runCommand(window.PMW.emails.commands.INITIATE_BLOCK_MANAGER);
    editor.runCommand(window.PMW.emails.commands.ADD_CONTENT_COMPONENT);
    editor.runCommand(window.PMW.emails.commands.START_UPDATING_SAVE_TIME);
    editor.runCommand(window.PMW.emails.commands.SYNC_UPDATED_DB_CONTENT);
    editor.runCommand(window.PMW.emails.commands.SYNC_GETTY_COUNT_FOR_COMPONENTS);

    await editor.runCommand(window.PMW.emails.commands.ADD_READONLY_FOOTER);

    addCssRules(opt);
    addDesignForDeprecatedPublishingFlow(editor);
    editor.runCommand(window.PMW.emails.commands.INIT_AUTOSAVE);
    editor.runCommand(window.PMW.emails.commands.INIT_CALENDAR_DIALOG);
    editor.runCommand(window.PMW.emails.commands.TOGGLE_BUY_GETTY_DESIGN_MESSAGE);
    await editor.runCommand(window.PMW.emails.commands.SET_LEGACY_MAIL_COMPATIBILITY);
    await editor.runCommand(window.PMW.emails.commands.UPDATE_BG_IMAGE_GMAIL_COMPATIBILITY);
    editor.runCommand(window.PMW.emails.commands.UPDATE_BUTTON_AOL_COMPATIBILITY);
    editor.runCommand(window.PMW.emails.commands.FIX_COMPONENT_FULL_WIDTH_OVERFLOW_AOL_YAHOO);
    editor.runCommand(window.PMW.emails.commands.SANITIZE_ALL_COMPONENTS);
    editor.runCommand(window.PMW.emails.commands.SET_TARGET_BLANK_FOR_ALL_LINKS);
    editor.runCommand(window.PMW.emails.commands.VALIDATE_ALL_TBODY_COMPONENTS);
    editor.runCommand(window.PMW.emails.commands.UPDATE_TABLE_OUTLOOK_COMPATIBILITY);
    editor.runCommand(window.PMW.emails.commands.GET_PENDING_MARKETING_POPUP);
    editor.runCommand(window.PMW.emails.commands.LISTENER_PASTE_AS_TEXT);
    editor.runCommand(window.PMW.emails.commands.COPY_MEDIA_QUERIES_TO_RESPONSIVE_STYLE, {
      component: editor.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER),
      responsiveStyles: ['width'],
    });
    editor.runCommand(window.PMW.emails.commands.FIX_VERTICAL_ALIGNMENT_VISUAL);

    editor.runCommand(window.PMW.emails.commands.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, {show: false});
    editor.runCommand(window.PMW.emails.commands.REMOVE_COMPONENT_LOADING_OVERLAYS_ON_LOAD);
    editor.runCommand(window.PMW.emails.commands.ASYNC_FETCH_ALL_COLOR_PALETTES);
    editor.runCommand(window.PMW.emails.commands.ASYNC_FETCH_ALL_COLOR_PALETTE_PILLS);
    editor.runCommand(window.PMW.emails.commands.APPLY_AUTO_UPDATES);
    editor.runCommand(window.PMW.emails.commands.FETCH_AI_REWRITE_RECENT_PROMPT);

    editor.UndoManager.clear();
    editor.runCommand(window.PMW.emails.commands.UPDATE_UNDO_REDO_STATES);
    editor.runCommand(window.PMW.emails.commands.ADD_PUPPETEER_LISTENERS);
    window.PMW.isInitializingEditor = false;
  });

  document.body.addEventListener(
    'touchstart',
    async (e: TouchEvent) => {
      if (shouldBlockInteractionInsideInactiveStyleManager(e)) {
        e.stopPropagation();
        e.preventDefault();
      }

      await editor.runCommand(window.PMW.emails.commands.TOGGLE_STYLEMANAGER_HEIGHT, {interactedEl: $(e.target as Element)});
      editor.stopCommand(window.PMW.emails.commands.TOGGLE_STYLEMANAGER_HEIGHT);
    },
    {passive: false}
  );

  window.PMW.onClick($('#html-editor-container'), '.js-spam-words-info', () => {
    editor.runCommand(window.PMW.emails.commands.VALIDATE_TEMPLATE_AND_PUBLISH);
  });

  window.PMW.onClick($('.js-embedded-email-done'), null, () => {
    editor.runCommand(window.PMW.emails.commands.SAVE_AND_CLOSE_EMBEDDED_EDITOR);
  });

  window.PMW.onClick($('.js-embedded-navbar-back-btn'), null, () => {
    editor.runCommand(window.PMW.emails.commands.CLOSE_EMBEDDED_EDITOR);
  });

  document.body.addEventListener('click', async (e: Event) => {
    const el = $(e.target as Element);
    const socialLinksDialog = $('.js-links-dialog-container');
    const addSocialLinkBtn = $('.js-add-social-link');

    if (shouldBlockInteractionInsideInactiveStyleManager(e)) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (
      !el.hasClass('js-add-social-link') &&
      socialLinksDialog.length > 0 &&
      socialLinksDialog.hasClass('-open') &&
      !(el.hasClass('js-links-dialog-container') || el.parents('.js-links-dialog-container').length > 0)
    ) {
      socialLinksDialog.removeClass('-open');
      addSocialLinkBtn.removeClass('-open');
    }

    if (!('ontouchstart' in document)) {
      await editor.runCommand(window.PMW.emails.commands.TOGGLE_STYLEMANAGER_HEIGHT, {interactedEl: el});
      editor.stopCommand(window.PMW.emails.commands.TOGGLE_STYLEMANAGER_HEIGHT);
    }

    return false;
  });

  // trigger a refresh of style manager to update values
  editor.on('undo redo', () => {
    editor.runCommand(window.PMW.emails.commands.REFRESH_STYLEMANAGER);
  });

  editor.on('canvas:dragdata', (dataTransfer: DataTransfer, result: CanvasDropData) => {
    setDroppedImagesMetaData(dataTransfer, result);
  });

  editor.on('canvas:drop', (dataTransfer: DataTransfer, model: Component[]) => {
    activateIfOneComponentDropped(model, editor);
  });

  const refreshStylesAfter = [
    'text:align-left',
    'text:align-center',
    'text:align-right',
    'text:align-justify',
    'text:toggle-bold',
    'text:toggle-italic',
    'text:toggle-underline',
    'text:toggle-strikethrough',
    'component:lock',
  ];

  editor.on('keymap:emit', (id: string, _shortcut: any, _event: KeyboardEvent) => {
    if (refreshStylesAfter.includes(id)) {
      editor.runCommand(window.PMW.emails.commands.REFRESH_STYLEMANAGER);
    }
  });
};

/**
 * Check if interaction should be blocked inside inactive style manager
 * @param e The event
 * @returns True if interaction should be blocked
 */
function shouldBlockInteractionInsideInactiveStyleManager(e: Event): boolean {
  const styleManagerEl = document.querySelector('.html-editor-style-manager');
  const isActive = styleManagerEl?.classList.contains('-active');
  const clickedInsideStyleManager = (e.target as Element)?.closest('.html-editor-style-manager');

  return !isActive && !!clickedInsideStyleManager;
}

/**
 * Check if target belongs to a specific class
 * @param target The jQuery element
 * @param className The class name to check
 * @returns True if target belongs to the class
 */
function doesTargetBelongInClass(target: JQuery<HTMLElement>, className: string): boolean {
  return target.hasClass(className) || target.parents(`.${className}`).length > 0;
}

/**
 * Activate component if only one was dropped
 * @param components The dropped components
 * @param editor The editor instance
 */
function activateIfOneComponentDropped(components: Component[], editor: Editor): void {
  if (components.length > 0) {
    editor.select(components[0]);

    components.forEach((component: Component) => {
      editor.runCommand(window.PMW.emails.commands.SET_IMAGE_LOADING, {component: component, loading: true});
    });
  }
}

/**
 * Set metadata for dropped images
 * @param dataTransfer The data transfer object
 * @param result The canvas drop result
 */
function setDroppedImagesMetaData(dataTransfer: DataTransfer, result: CanvasDropData): void {
  if (typeof result.content === 'object' && result.content.length > 0) {
    for (let i = 0; i < result.content.length; i++) {
      if (result.content[i].type === 'image') {
        const identifier = getAlphaNumId();
        result.content[i].type = window.PMW.emails.components.graphic.PMW_IMAGE;
        result.content[i].attributes[window.PMW.emails.props.DROP_IDENTIFIER] = identifier;
        result.content[i].file[window.PMW.emails.props.DROP_IDENTIFIER] = identifier;
      }
    }
  }
}

function getAlphaNumId(): string {
  return Math.random().toString(36).slice(2);
}

/**
 * Add design for deprecated publishing flow
 * @param editor The editor instance
 */
function addDesignForDeprecatedPublishingFlow(editor: Editor): void {
  const designID = window.localStorage.getItem(window.PMW.emails.LOCAL_STORAGE_POSTER_ID_KEY);
  const pageID = window.localStorage.getItem(window.PMW.emails.LOCAL_STORAGE_POSTER_PAGE_ID_KEY) ?? undefined;
  if (!designID) {
    return;
  }

  if (window.PMW.enableMyStuffMultipagesUI && !pageID) {
    console.error('No pageID found ');
    return;
  }

  const buttons = getAddDesignToEmailTemplateModalButtons(editor, designID, pageID);
  const dialogContent = window.PMW.openModalDialog(window.PMW.emails.ADD_DESIGN_TO_EMAIL_TEMPLATE, i18next.t('pmwjs_add_design_template'), buttons, true, '-modal-650', false).find(
    '.content'
  );

  window.PMW.load(dialogContent, window.PMW.util.site_url(`emailmaker/addDesignToEmailTemplateDialog/${designID}${pageID ? '/' + pageID : ''}`), {}, function (): void {
    window.localStorage.removeItem(window.PMW.emails.LOCAL_STORAGE_POSTER_ID_KEY);
    window.localStorage.removeItem(window.PMW.emails.LOCAL_STORAGE_POSTER_PAGE_ID_KEY);
    if ($('#add-design-template').length === 0) {
      console.error('Poster with design ID not found');
      window.PMW.closeModalDialog(window.PMW.emails.ADD_DESIGN_TO_EMAIL_TEMPLATE).then(function (): void {
        void window.PMW.openErrorModal({
          message: i18next.t('pmwjs_adding_design_error'),
        });
      }, noop);
    }
  });
}

/**
 * Appends a design block onto canvas and sets given design ID
 * @param editor The editor instance
 * @param id The design ID
 * @param pageId
 */
function addDesignWithId(editor: Editor, id: string, pageId?: string): void {
  const block = editor.BlockManager.get(window.PMW.emails.blocks.ID_DESIGN);
  const isActivatedOnLoad = block.get('activate');

  block.set('activate', false);
  const component = appendBlockOnClick(block, editor, {}, 0);
  component?.set(window.PMW.emails.props.DESIGN_ID, id);
  component?.set(window.PMW.emails.props.DESIGN_PAGE_ID, pageId);
  block.set('activate', isActivatedOnLoad);

  editor.runCommand(window.PMW.emails.commands.RELOAD_MODIFIED_DESIGN, {component});
}

/**
 * Returns the buttons to be used for adding design to email template modal
 * @param editor The editor instance
 * @param designID The design ID
 * @param designPageID The page ID
 * @returns The modal buttons configuration
 */
function getAddDesignToEmailTemplateModalButtons(editor: Editor, designID: string, designPageID?: string): Record<string, {ch: VoidFunction; classes: string}> {
  const button: Record<string, {ch: VoidFunction; classes: string}> = {};
  button[i18next.t('pmwjs_no_dont_add')] = {
    ch: function (): void {
      void window.PMW.closeModalDialog(window.PMW.emails.ADD_DESIGN_TO_EMAIL_TEMPLATE);
    },
    classes: '-go-back secondary',
  };
  button[i18next.t('pmwjs_yes_add_my_design')] = {
    ch: function (): void {
      addDesignWithId(editor, designID, designPageID);
      void window.PMW.closeModalDialog(window.PMW.emails.ADD_DESIGN_TO_EMAIL_TEMPLATE);
    },
    classes: '-add-design',
  };
  return button;
}
